import type { AlertType, EngagementAssessment, RiskTier } from '../types';

export type AccountSelectionMode = 'all' | 'include' | 'exclude';

export interface AccountSelection {
  mode: AccountSelectionMode;
  values: string[];
}

export type EngagementHealthView = 'risk' | 'near_completion' | 'alerts' | 'digests';

export const BLANK_ENGAGEMENT_STATUS = '(blank)';
// Blank is included because a never-populated status counts as open in the
// backend rules (isOpenEngagementStatus) — those engagements are scored and
// alerting, so the default view must not hide them.
export const DEFAULT_ENGAGEMENT_STATUSES = ['Active', 'On Hold', BLANK_ENGAGEMENT_STATUS];
export const KPI_ALERT_EXCLUDED_ACCOUNT_ROLLUPS = ['AbbVie'];

export function formatDigestRunLabel(periodKey: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(periodKey);
  if (!match) return periodKey;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) return periodKey;
  return `${date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })} ${day} run`;
}

export function formatDigestSchedule(cron: string, timezone: string): string {
  if (cron.trim() === '0 8 1,15 * *' && timezone === 'America/New_York') {
    return '1st & 15th at 8:00 AM ET';
  }
  return `${cron} ${timezone}`.trim();
}

export function formatDigestNextRun(value: string | null, timezone: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(date);
}

export interface ActiveEngagementSummary {
  engagements: number;
  counts: {
    stalledStart: number;
    nearCompletion: number;
    staleActivity: number;
    staleActivity30d: number;
  };
}

/** Top-of-page totals intentionally describe Active engagements only. */
export function summarizeActiveEngagements(
  engagements: EngagementAssessment[],
  excludedAlertAccounts: string[] = KPI_ALERT_EXCLUDED_ACCOUNT_ROLLUPS,
): ActiveEngagementSummary {
  const summary: ActiveEngagementSummary = {
    engagements: 0,
    counts: { stalledStart: 0, nearCompletion: 0, staleActivity: 0, staleActivity30d: 0 },
  };
  const excluded = new Set(excludedAlertAccounts.map(normalizeAccountKey));

  for (const engagement of engagements) {
    if (engagement.engagementStatus?.trim() !== 'Active') continue;
    summary.engagements += 1;
    if (excluded.has(normalizeAccountKey(accountNameForDisplay(engagement)))) continue;
    const signalTypes = new Set(engagement.riskSignals.map((signal) => signal.type));
    if (signalTypes.has('stalled_start')) summary.counts.stalledStart += 1;
    if (signalTypes.has('near_completion')) summary.counts.nearCompletion += 1;
    if (signalTypes.has('stale_activity')) summary.counts.staleActivity += 1;
    if (signalTypes.has('stale_activity_30d')) summary.counts.staleActivity30d += 1;
  }

  return summary;
}

function normalizeAccountKey(value: string | null | undefined): string {
  const withoutObsoleteMarker = value?.trim().toLowerCase().replace(/\bdo\s+not\s+use\b.*$/, '') ?? '';
  const words = withoutObsoleteMarker
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const legalSuffixes = new Set([
    'co', 'company', 'corp', 'corporation', 'inc', 'incorporated',
    'llc', 'llp', 'ltd', 'limited', 'plc',
  ]);
  while (words.length > 1 && legalSuffixes.has(words.at(-1) ?? '')) words.pop();
  return words.join(' ');
}

/** True when the status filter matches the default set (any order). */
export function isDefaultStatuses(statuses: string[]): boolean {
  return (
    statuses.length === DEFAULT_ENGAGEMENT_STATUSES.length &&
    DEFAULT_ENGAGEMENT_STATUSES.every((status) => statuses.includes(status))
  );
}

/**
 * Frontend mirror of riskTierForScore in
 * src/reports/engagement-health/rules.ts — keep the boundaries in sync.
 */
export function riskTierForScore(score: number | null): RiskTier {
  if (score == null) return 'not_scored';
  if (score === 0) return 'healthy';
  if (score < 30) return 'watch';
  if (score < 60) return 'at_risk';
  return 'critical';
}

export interface EngagementRiskFilters {
  accounts: AccountSelection;
  affiliate: string[];
  owners: string[];
  csms: string[];
  responsible: string[];
  signalTypes: AlertType[];
  statuses: string[];
  riskTiers: RiskTier[];
  opportunity: string;
  sowFrom: string;
  sowTo: string;
}

export const ALL_ACCOUNTS: AccountSelection = { mode: 'all', values: [] };

export function accountNameForDisplay(
  engagement: Pick<EngagementAssessment, 'accountDisplayName' | 'accountName'>,
): string | null {
  return engagement.accountDisplayName?.trim() || engagement.accountName?.trim() || null;
}

export function accountIsSelected(selection: AccountSelection, account: string | null): boolean {
  if (selection.mode === 'all') return true;
  // Exclude mode is subtractive: rows without an account stay visible unless
  // they are explicitly excluded. Include mode is additive, so an unnamed
  // account cannot match one of its selected values.
  if (selection.mode === 'exclude') return !account || !selection.values.includes(account);
  return !!account && selection.values.includes(account);
}

export function toggleAccountSelection(
  selection: AccountSelection,
  account: string,
  checked: boolean,
): AccountSelection {
  const values = new Set(selection.values);
  if (selection.mode === 'all') {
    return checked ? selection : { mode: 'exclude', values: [account] };
  }
  if (selection.mode === 'exclude') {
    if (checked) values.delete(account);
    else values.add(account);
    return values.size === 0 ? ALL_ACCOUNTS : { mode: 'exclude', values: [...values].sort() };
  }
  if (checked) values.add(account);
  else values.delete(account);
  return { mode: 'include', values: [...values].sort() };
}

export function selectedAccountCount(selection: AccountSelection, universeSize: number): number {
  if (selection.mode === 'all') return universeSize;
  if (selection.mode === 'exclude') return Math.max(0, universeSize - selection.values.length);
  return selection.values.length;
}
export function engagementStatusValue(status: string | null): string {
  return status?.trim() || BLANK_ENGAGEMENT_STATUS;
}

export function filterEngagementAssessments(
  engagements: EngagementAssessment[],
  filters: EngagementRiskFilters,
): EngagementAssessment[] {
  const needle = filters.opportunity.trim().toLowerCase();

  return sortEngagementAssessments(
    engagements.filter((engagement) => {
      if (
        filters.statuses.length > 0 &&
        !filters.statuses.includes(engagementStatusValue(engagement.engagementStatus))
      ) return false;
      if (filters.riskTiers.length > 0 && !filters.riskTiers.includes(engagement.riskTier)) return false;
      if (!accountIsSelected(filters.accounts, accountNameForDisplay(engagement))) return false;
      if (
        filters.affiliate.length > 0 &&
        !engagement.deliveringAffiliates.some(
          (affiliate) => affiliate.name && filters.affiliate.includes(affiliate.name),
        )
      ) return false;
      const ownerKey = engagement.ownerId ?? engagement.ownerName;
      if (filters.owners.length > 0 && (!ownerKey || !filters.owners.includes(ownerKey))) return false;
      const csmKey = engagement.csmId ?? engagement.csmName;
      if (filters.csms.length > 0 && (!csmKey || !filters.csms.includes(csmKey))) return false;
      if (filters.responsible.length > 0) {
        const people = [
          engagement.ownerEmail?.toLowerCase(),
          engagement.ownerId,
          engagement.ownerName,
          engagement.csmEmail?.toLowerCase(),
          engagement.csmId,
          engagement.csmName,
        ].filter((value): value is string => !!value);
        if (!filters.responsible.some((value) => people.includes(value.toLowerCase()) || people.includes(value))) {
          return false;
        }
      }
      if (
        filters.signalTypes.length > 0 &&
        !filters.signalTypes.every((type) =>
          engagement.riskSignals.some((signal) => signal.type === type),
        )
      ) return false;
      if (needle) {
        const haystack = [
          engagement.opportunityId,
          engagement.opportunityName,
          engagement.accountName,
          engagement.accountRollupName,
          engagement.accountDisplayName,
          engagement.coacheeName,
          engagement.ownerName,
          engagement.csmName,
          ...engagement.projectNumbers,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filters.sowFrom && (!engagement.sowStartDate || engagement.sowStartDate < filters.sowFrom)) {
        return false;
      }
      if (filters.sowTo && (!engagement.sowStartDate || engagement.sowStartDate > filters.sowTo)) {
        return false;
      }
      return true;
    }),
  );
}

export function sortEngagementAssessments(
  engagements: EngagementAssessment[],
): EngagementAssessment[] {
  return [...engagements].sort((a, b) => {
    const scoreA = a.riskScore ?? -1;
    const scoreB = b.riskScore ?? -1;
    if (scoreA !== scoreB) return scoreB - scoreA;

    const endA = a.sowEndDate ?? '9999-12-31';
    const endB = b.sowEndDate ?? '9999-12-31';
    if (endA !== endB) return endA.localeCompare(endB);

    const account = (accountNameForDisplay(a) ?? '').localeCompare(accountNameForDisplay(b) ?? '');
    if (account !== 0) return account;
    return (a.opportunityName ?? a.opportunityId).localeCompare(b.opportunityName ?? b.opportunityId);
  });
}

export function sortNearCompletion(engagements: EngagementAssessment[]): EngagementAssessment[] {
  return [...engagements].sort((a, b) => {
    const pct = (b.pctComplete ?? -1) - (a.pctComplete ?? -1);
    if (pct !== 0) return pct;
    const end = (a.sowEndDate ?? '9999-12-31').localeCompare(b.sowEndDate ?? '9999-12-31');
    if (end !== 0) return end;
    return (a.opportunityName ?? a.opportunityId).localeCompare(b.opportunityName ?? b.opportunityId);
  });
}

const ARRAY_PARAM_MAP = {
  signal: 'signalTypes',
  owner: 'owners',
  csm: 'csms',
  responsible: 'responsible',
  affiliate: 'affiliate',
  status: 'statuses',
  risk: 'riskTiers',
} as const;

export function readHealthUrlState(params: URLSearchParams): {
  view: EngagementHealthView;
  filters: EngagementRiskFilters & { showSnoozed: boolean };
} {
  const rawView = params.get('view');
  const view: EngagementHealthView = rawView === 'near_completion' || rawView === 'alerts' || rawView === 'digests'
    ? rawView
    : 'risk';
  const mode = params.get('account_mode');
  const accountMode: AccountSelectionMode = mode === 'include' || mode === 'exclude' ? mode : 'all';
  const statuses = params.get('status_all') === '1'
    ? []
    : params.has('status') ? params.getAll('status') : [...DEFAULT_ENGAGEMENT_STATUSES];
  return {
    view,
    filters: {
      accounts: { mode: accountMode, values: params.getAll('account') },
      affiliate: params.getAll('affiliate'),
      owners: params.getAll('owner'),
      csms: params.getAll('csm'),
      responsible: params.getAll('responsible'),
      signalTypes: params.getAll('signal') as AlertType[],
      statuses,
      riskTiers: params.getAll('risk') as RiskTier[],
      opportunity: params.get('q') ?? '',
      sowFrom: params.get('sow_from') ?? '',
      sowTo: params.get('sow_to') ?? '',
      showSnoozed: params.get('snoozed') === '1',
    },
  };
}

export function writeHealthUrlState(
  view: EngagementHealthView,
  filters: EngagementRiskFilters & { showSnoozed: boolean },
): URLSearchParams {
  const params = new URLSearchParams();
  if (view !== 'risk') params.set('view', view);
  if (filters.accounts.mode !== 'all') params.set('account_mode', filters.accounts.mode);
  filters.accounts.values.forEach((value) => params.append('account', value));
  for (const [param, key] of Object.entries(ARRAY_PARAM_MAP) as Array<[keyof typeof ARRAY_PARAM_MAP, typeof ARRAY_PARAM_MAP[keyof typeof ARRAY_PARAM_MAP]]>) {
    if (key === 'statuses') continue;
    (filters[key] as string[]).forEach((value) => params.append(param, value));
  }
  if (filters.statuses.length === 0) params.set('status_all', '1');
  else if (!isDefaultStatuses(filters.statuses)) filters.statuses.forEach((value) => params.append('status', value));
  if (filters.opportunity) params.set('q', filters.opportunity);
  if (filters.sowFrom) params.set('sow_from', filters.sowFrom);
  if (filters.sowTo) params.set('sow_to', filters.sowTo);
  if (filters.showSnoozed) params.set('snoozed', '1');
  return params;
}
